'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Copy, Check, LogIn, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const DEMO_CREDENTIALS = [
  { role: 'Senior Student', email: 'maya.k@stateuniversity.edu', password: 'Senior2026!' },
  { role: 'Photographer', email: 'admin@seniorshots.co', password: 'ShotsAdmin#1' },
];

interface Props {
  onSwitchMode: () => void;
}

export default function LoginForm({ onSwitchMode }: Props) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const handleCopy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const autofill = (cred: typeof DEMO_CREDENTIALS[0]) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    toast.success(`Autofilled as ${cred.role}`);
  };

  // Backend integration point: POST /api/auth/login
  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setIsLoading(false);

    const adminCred = DEMO_CREDENTIALS.find((c) => c.role === 'Photographer');
    if (data.email === adminCred?.email && data.password === adminCred?.password) {
      toast.success('Welcome back! Redirecting to dashboard…');
      setTimeout(() => router.push('/admin-dashboard'), 800);
    } else if (DEMO_CREDENTIALS.some((c) => c.email === data.email && c.password === data.password)) {
      toast.success('Welcome back! Redirecting to booking…');
      setTimeout(() => router.push('/booking-scheduling-screen'), 800);
    } else {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
    }
  };

  return (
    <div>
      <div className="mb-7">
        <h2 className="font-display text-3xl text-white mb-1.5">Welcome back</h2>
        <p className="text-gray-400 text-sm">
          Sign in to manage your booking or{' '}
          <button onClick={onSwitchMode} className="text-[#c9a96e] hover:underline font-medium">
            create an account
          </button>
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            University Email
          </label>
          <input
            type="email"
            placeholder="yourname@university.edu"
            className={`input-field bg-[#1a1a2e] border-[#2e2e4a] text-white placeholder-gray-500 focus:border-[#c9a96e] ${errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-gray-300">Password</label>
            <button type="button" className="text-xs text-[#c9a96e] hover:underline">
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className={`input-field pr-11 bg-[#1a1a2e] border-[#2e2e4a] text-white placeholder-gray-500 focus:border-[#c9a96e] ${errors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'At least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-400">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            id="rememberMe"
            type="checkbox"
            className="w-4 h-4 rounded border-gray-600 text-[#c9a96e] focus:ring-[#c9a96e] cursor-pointer bg-[#1a1a2e]"
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="text-sm text-gray-400 cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#1a1a2e] text-[#c9a96e] py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-150 hover:bg-[#252540] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              <LogIn size={16} />
              Sign In
            </>
          )}
        </button>
      </form>

      {/* Admin link */}
      <p className="text-center text-xs text-gray-500 mt-5">
        Photographer?{' '}
        <button
          onClick={() => {
            autofill(DEMO_CREDENTIALS[1]);
          }}
          className="text-[#c9a96e] hover:underline"
        >
          Use admin credentials
        </button>
      </p>

      {/* Demo credentials */}
      <div className="mt-6 bg-[#1a1a2e] border border-[#2e2e4a] rounded-xl p-4">
        <p className="text-xs font-semibold text-[#c9a96e] mb-3 uppercase tracking-wide">
          Demo Accounts
        </p>
        <div className="space-y-3">
          {DEMO_CREDENTIALS.map((cred) => (
            <div key={`cred-${cred.role}`} className="bg-[#0f0f1a] rounded-lg border border-[#2e2e4a] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-300">{cred.role}</span>
                <button
                  onClick={() => autofill(cred)}
                  className="text-xs bg-[#c9a96e] text-[#1a1a2e] px-2.5 py-1 rounded-md hover:bg-[#d4b87a] transition-colors font-semibold"
                >
                  Use
                </button>
              </div>
              <div className="space-y-1">
                {[
                  { label: 'Email', value: cred.email, key: `email-${cred.role}` },
                  { label: 'Password', value: cred.password, key: `pass-${cred.role}` },
                ].map((field) => (
                  <div key={field.key} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 w-14 shrink-0">{field.label}</span>
                    <span className="text-xs text-gray-300 font-mono truncate flex-1">{field.value}</span>
                    <button
                      onClick={() => handleCopy(field.value, field.key)}
                      className="text-gray-500 hover:text-gray-300 shrink-0 transition-colors"
                    >
                      {copiedField === field.key ? (
                        <Check size={13} className="text-green-400" />
                      ) : (
                        <Copy size={13} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}